/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  BodyMedium,
  BodySmall,
  DeprecatedCheckIcon,
  HeadingMedium,
  HeadingSmall,
  Modal,
  theme,
  useToast,
} from '@arnold/common';
import { ProductCode } from '@arnold/core';
import styled from '@emotion/styled';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { FormRadioInput } from '../../components/Common';
import {
  AddToOnboardingOrProcessSurveyModal,
  AddToOneTimeSurveysModal,
  NO_ONBOARDING_VALUE,
  ScheduledOneTimeSurvey,
  ScheduledProcessSurvey,
} from '../../components/Modals/OrganizationImport/AddEmployeesToSurveys';
import {
  ContactType,
  OrganizationQuery,
  OrganizationTeamsQuery,
  ProcessStatus,
  SurveyGroupsQuery,
  SurveysQuery,
  SurveyStatus,
  TopicGroupTypeCode,
  useImportOrganizationMutation,
} from '../../generated/hooks';
import { getOrganizationSettingsReport, getOrganizationStructure, getReportRecipients } from '../../graphql/queries';
import {
  getCreditPriceForSurveyGroup,
  ONE_TIME_PULSER_SURVEY_CREDIT_PRICE,
  ONE_TIME_SURVEY_CREDIT_PRICE,
} from '../../lib/creditPricing';
import { isPulserTopicGroup } from '../../lib/helpers/pulser';
import {
  constructEmployees,
  constructTeams,
  EmployeeRecord,
  EmployeeRecordWithId,
  getOrganizationImportColumnWidth,
  TeamRecord,
} from './OrganizationImport';
import {
  areEmployeeFieldValuesEqual,
  createAdditionalDataForEmployees,
  createAdditionalDataforTeams,
  EmployeeImportState,
  EmployeeRecordWithAdditionalData,
  TeamImportState,
} from './OrgImportUtils';

interface OrganizationImportSaveModalsProps {
  onHide: () => void;
  employees: EmployeeRecord[];
  teams: TeamRecord[];
  organizationTeams: NonNullable<OrganizationTeamsQuery['organizationTeams']>;
  organization: NonNullable<OrganizationQuery['organization']>;
  surveyGroups: NonNullable<SurveyGroupsQuery['organization']>['surveyGroups'];
  surveys: NonNullable<SurveysQuery['user']['organization']>['surveys'];
  refetch: () => void;
  setFinalStep: (isFinalStep: boolean) => void;
}

export const OrganizationImportSaveModals = ({
  onHide,
  employees,
  teams,
  organizationTeams,
  organization,
  surveyGroups,
  surveys,
  refetch,
  setFinalStep,
}: OrganizationImportSaveModalsProps) => {
  const { t, i18n } = useTranslation('organizationImportModals');
  const { addToast } = useToast();
  const history = useHistory();
  const [nextButtonIsDisabled, setNextButtonIsDisabled] = useState(false);
  const [checkEditedRespondentsMarkRed, setCheckEditedRespondentsMarkRed] = useState(0);
  const [onboardingsScheduledForEmployees, setOnboardingsScheduledForRespondents] = useState<ScheduledProcessSurvey[]>(
    [],
  );
  const [oneTimeSurveysScheduledForEmployees, setOneTimeSurveysScheduledForRespondents] = useState<
    ScheduledOneTimeSurvey[]
  >([]);
  const [processSurveysScheduledForEmployees, setProcessSurveysScheduledForRespondents] = useState<
    ScheduledProcessSurvey[]
  >([]);
  const [importingProgressPercentage, setImportingProgressPercentage] = useState(0);

  const notArchivedSurveyGroups = surveyGroups.filter((sg) => sg.status !== ProcessStatus.Archived);
  const onboardings = notArchivedSurveyGroups.filter((sg) => sg.topicGroup.typeCode === TopicGroupTypeCode.Onboarding);
  const processSurveys = notArchivedSurveyGroups.filter(
    (sg) => sg.topicGroup.typeCode !== TopicGroupTypeCode.Onboarding,
  );
  const ongoingSurveys = surveys.filter(
    (survey) => (survey.status === SurveyStatus.Ongoing || survey.status === SurveyStatus.Prepared) && !survey.public,
  );

  const employeesWithAdditionalData = useMemo(
    () => createAdditionalDataForEmployees(employees, organizationTeams),
    [employees, organizationTeams],
  );

  const teamsWithAdditionalData = useMemo(
    () => createAdditionalDataforTeams(teams, organizationTeams),
    [teams, organizationTeams],
  );

  const employeesWithUnsureState = employeesWithAdditionalData.filter((e) => e.state === EmployeeImportState.NotSure);

  const [matchChoicesByUser, setMatchChoicesByUser] = useState<
    {
      newEmployee: EmployeeRecordWithAdditionalData;
      oldEmployee: EmployeeRecordWithId;
      choice: EmployeeImportState.Edited | EmployeeImportState.New;
    }[]
  >([]);

  const updateMatchChoicesByUser = (
    newEmployee: EmployeeRecordWithAdditionalData,
    oldEmployee: EmployeeRecordWithId,
    choice: EmployeeImportState.Edited | EmployeeImportState.New,
  ) => {
    setMatchChoicesByUser((prev) => {
      const existingChoice = prev.find((m) => m.newEmployee === newEmployee && m.oldEmployee === oldEmployee);
      if (existingChoice) {
        return prev.map((m) => (m === existingChoice ? { ...m, choice } : m));
      }
      return [...prev, { newEmployee, oldEmployee, choice }];
    });
  };

  const allSteps = useMemo<StepType[]>(() => {
    const steps: StepType[] = [];
    if (employeesWithUnsureState.length > 0) {
      steps.push(StepType.CheckEditedRespondents);
    }
    if (
      employeesWithAdditionalData
        .filter((employee) => employee.surveyParticipant)
        .some((employee) => employee.state === EmployeeImportState.New) ||
      matchChoicesByUser
        .filter((m) => m.newEmployee.surveyParticipant)
        .some((m) => m.choice === EmployeeImportState.New)
    ) {
      if (onboardings.length > 0) {
        steps.push(StepType.Onboarding);
      }
      if (ongoingSurveys.length > 0) {
        steps.push(StepType.OneTimeSurvey);
      }
      if (processSurveys.length > 0) {
        steps.push(StepType.ProcessSurvey);
      }
    }
    steps.push(StepType.ImportInfo);
    steps.push(StepType.Importing);
    return steps;
  }, [
    employeesWithUnsureState,
    employeesWithAdditionalData,
    onboardings,
    processSurveys,
    ongoingSurveys,
    matchChoicesByUser,
  ]);

  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const currentStep = allSteps[currentStepIdx];
  useEffect(() => {
    setFinalStep(currentStep === StepType.Importing);
  }, [currentStep, setFinalStep]);

  const [importOrg] = useImportOrganizationMutation({
    refetchQueries: [
      {
        query: getOrganizationStructure,
        variables: { organizationId: organization.id },
      },
      {
        query: getOrganizationSettingsReport,
        variables: { organizationId: organization.id },
      },
      {
        query: getReportRecipients,
        variables: { organizationId: organization.id },
      },
    ],
  });

  const getEmployeeStringForCompare = (employee: EmployeeRecordWithAdditionalData) =>
    `${employee.firstname} ${employee.surname} ${employee.team} ${employee.email} ${employee.otherEmail} ${employee.phoneNumber}`;

  useEffect(() => {
    if (currentStep === StepType.Importing) {
      // simulate importing progress percentage
      // 500ms for normal delay + 4ms for each employee + 0.001ms for each employee * employee
      // coz the more employees we have the more time it takes to import (exponentially)
      const importingTimeEstimate = 300 + employees.length * 4 + 0.001 * (employees.length * employees.length);
      // update every 500ms
      const interval = setInterval(() => {
        setImportingProgressPercentage((prev) => {
          let newPercentage = prev + Math.floor(100 / (importingTimeEstimate / 500));
          if (newPercentage >= 95) {
            newPercentage = 95;
            clearInterval(interval);
          }
          return newPercentage;
        });
      }, 500);

      // start the importing process

      // copy employees
      const employeesCopy = employeesWithAdditionalData.map((employee) => ({
        ...employee,
      }));
      // assign IDs to edited employees
      employeesCopy.forEach((employee) => {
        if (employee.state === EmployeeImportState.Edited || employee.state === EmployeeImportState.Unchanged) {
          employee.id = employee.originalEmployeeRecord!.id;
        } else if (employee.state === EmployeeImportState.NotSure) {
          const match = matchChoicesByUser.find((m) => m.newEmployee === employee);
          if (match && match.choice === EmployeeImportState.Edited) {
            employee.id = match.oldEmployee.id;
          }
        }
      });

      const newEmployees = employeesCopy.filter((e) => e.id === undefined);
      // assign surveyIds to new employees
      oneTimeSurveysScheduledForEmployees.forEach((survey) => {
        const surveyEmployeeString = getEmployeeStringForCompare(survey.employee);
        const newEmployee = newEmployees.find((e) => getEmployeeStringForCompare(e) === surveyEmployeeString);
        if (!newEmployee) {
          return alert('Employee not found during construction of one time survey for new employee'); // TODO: remove when in done
        }
        newEmployee.surveyIds.push(survey.surveyId);
      });
      // assign onboardings and process surveys to new employees
      [...onboardingsScheduledForEmployees, ...processSurveysScheduledForEmployees].forEach((survey) => {
        if (survey.surveyGroupId === NO_ONBOARDING_VALUE) {
          // Do not assign onboarding to new employees if it is no onboarding
          return;
        }
        const surveyEmployeeString = getEmployeeStringForCompare(survey.employee);
        const newEmployee = newEmployees.find((e) => getEmployeeStringForCompare(e) === surveyEmployeeString);
        if (!newEmployee) {
          return alert('Employee not found during construction of onboardings and process surveys for new employee'); // TODO: remove when in done
        }
        newEmployee.surveyGroups.push({ surveyGroupId: survey.surveyGroupId, addedOn: survey.addedOn });
      });

      importOrg({
        variables: {
          orgId: organization.id,
          respondents: employeesCopy.map((employee) => ({
            firstname: employee.firstname,
            surname: employee.surname,
            team: employee.team,
            id: employee.id,
            email: employee.email,
            otherEmail: employee.otherEmail,
            countryCallingCode: employee.countryCallingCode.toString(),
            phoneNumber: employee.phoneNumber.toString(),
            surveyGroups: employee.surveyGroups,
            surveyIds: employee.surveyIds,
            managerOfTeam: employee.managerOfTeam,
            preferredContactType:
              employee.preferredContact === 'EMAIL'
                ? ContactType.Email
                : employee.preferredContact === 'SMS'
                  ? ContactType.Sms
                  : null,
            disabled: !employee.surveyParticipant,
            language: employee.language.toLowerCase(),
          })),
          teams: teams.map((team) => ({
            id: team.id,
            name: team.name,
            supervisorTeam: team.supervisorTeam,
          })),
        },
      })
        .then(() => {
          clearInterval(interval);
          setImportingProgressPercentage(100);
          setTimeout(() => {
            refetch?.();
            addToast(t('importDone'), 7000);
            history.push('/teams');
          }, 750);
        })
        .catch((err) => {
          console.error(err);
          onHide();
          addToast(t('importFailed'), 7000);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]); // only run on first render with currentStep === importing

  const checkEditedRespondentsStepContent = (
    <>
      <HeadingSmall>{t('employeesWithChangedDataHeading')}</HeadingSmall>
      <BodyMedium>{t('employeesWithChangedDataDescription')}</BodyMedium>
      {employeesWithUnsureState.map((employee) => {
        return employee.possibleMatches!.map((match) => {
          const { choice } =
            matchChoicesByUser.find((m) => m.newEmployee === employee && m.oldEmployee === match) ?? {};
          // skip rendering of the employee match choice if the newEmployee is already in the matchChoicesByUser as edited with different oldEmployee
          // or if the oldEmployee is already in the matchChoicesByUser as edited with different newEmployee
          const isAlreadyEdited = matchChoicesByUser.some(
            (m) =>
              m.choice === EmployeeImportState.Edited &&
              ((m.newEmployee === employee && m.oldEmployee !== match) ||
                (m.oldEmployee === match && m.newEmployee !== employee)),
          );
          if (isAlreadyEdited) {
            return null;
          }
          return (
            <MatchChoiceComponent
              newEmployee={employee}
              oldEmployee={match}
              choice={choice}
              onChoiceChange={(choice) => updateMatchChoicesByUser(employee, match, choice)}
              redMarkNoChoice={checkEditedRespondentsMarkRed}
            />
          );
        });
      })}
    </>
  );

  const originalEmployeesCount = useMemo(
    () => constructEmployees(organizationTeams, false).length,
    [organizationTeams],
  );
  const originalTeamsCount = useMemo(() => constructTeams(organizationTeams).length, [organizationTeams]);

  const employeesCreatedCount = employeesWithAdditionalData.filter(
    (employee) =>
      employee.state === EmployeeImportState.New ||
      (employee.state === EmployeeImportState.NotSure &&
        matchChoicesByUser.some((m) => m.newEmployee === employee && m.choice === EmployeeImportState.New)),
  ).length;

  const teamsCreatedCount = teamsWithAdditionalData.filter((team) => team.state === TeamImportState.New).length;

  const employeesEditedCount = employeesWithAdditionalData.filter(
    (employee) =>
      employee.state === EmployeeImportState.Edited ||
      (employee.state === EmployeeImportState.NotSure &&
        !matchChoicesByUser.some((m) => m.newEmployee === employee && m.choice === EmployeeImportState.New)),
  ).length;
  const teamsEditedCount = teamsWithAdditionalData.filter((team) => team.state === TeamImportState.Edited).length;
  const employeesUnchangedCount = employeesWithAdditionalData.filter(
    (employee) => employee.state === EmployeeImportState.Unchanged,
  ).length;
  const teamsUnchangedCount = teamsWithAdditionalData.filter((team) => team.state === TeamImportState.Unchanged).length;

  // deleted = original - edited - unchanged
  const employeesDeletedCount = originalEmployeesCount - employeesEditedCount - employeesUnchangedCount;

  const teamsDeletedCount = originalTeamsCount - teamsEditedCount - teamsUnchangedCount;

  const employeesAddedToSurveys: { name: string; count: number; creditsPerParticipant: number | null }[] = [];
  // onboardings
  onboardingsScheduledForEmployees.forEach((onboarding) => {
    const onb = onboardings.find((o) => o.id === onboarding.surveyGroupId);
    if (onb) {
      const name = onb.topicGroup.name;
      const existing = employeesAddedToSurveys.find((e) => e.name === name);
      if (existing) {
        existing.count++;
      } else {
        employeesAddedToSurveys.push({
          name,
          count: 1,
          creditsPerParticipant: getCreditPriceForSurveyGroup(onb.topicGroup.typeCode),
        });
      }
    }
  });
  // one time surveys
  oneTimeSurveysScheduledForEmployees.forEach((oneTimeSurvey) => {
    const survey = ongoingSurveys.find((o) => o.id === oneTimeSurvey.surveyId);
    if (survey) {
      const name = survey.topicGroup?.name ?? '';
      const existing = employeesAddedToSurveys.find((e) => e.name === name);
      if (existing) {
        existing.count++;
      } else {
        employeesAddedToSurveys.push({
          name,
          count: 1,
          creditsPerParticipant: isPulserTopicGroup(survey.topicGroup)
            ? ONE_TIME_PULSER_SURVEY_CREDIT_PRICE
            : ONE_TIME_SURVEY_CREDIT_PRICE,
        });
      }
    }
  });
  // process surveys
  processSurveysScheduledForEmployees.forEach((processSurvey) => {
    const ps = processSurveys.find((p) => p.id === processSurvey.surveyGroupId);
    if (ps) {
      const name = ps.topicGroup.name;
      const existing = employeesAddedToSurveys.find((e) => e.name === name);
      if (existing) {
        existing.count++;
      } else {
        employeesAddedToSurveys.push({
          name,
          count: 1,
          creditsPerParticipant: getCreditPriceForSurveyGroup(ps.topicGroup.typeCode),
        });
      }
    }
  });

  const isCreditsOrg = organization.productVersion?.product === ProductCode.Credits;
  const totalCredits = employeesAddedToSurveys.reduce((sum, entry) => {
    return entry.creditsPerParticipant !== null ? sum + entry.count * entry.creditsPerParticipant : sum;
  }, 0);

  const formatCreditsLabel = (amount: number) => {
    const formattedCount = amount.toLocaleString(i18n.language, { maximumFractionDigits: 2 });
    return Number.isInteger(amount)
      ? t('surveyCredits', { count: amount, formattedCount, postProcess: 'interval' })
      : t('surveyCreditsDecimal', { formattedCount });
  };

  const ImportInfoStepContent = (
    <>
      <HeadingSmall>{t('organizationImportCard:allChanges')}</HeadingSmall>
      <ChangesInfoWrapper>
        <ChangesInfo
          dataCy="import-modal-employees"
          title={t('ofEmployees')}
          total={employees.length}
          created={employeesCreatedCount}
          deleted={employeesDeletedCount}
          edited={employeesEditedCount}
          unchanged={employeesUnchangedCount}
        />
        <ChangesInfo
          dataCy="import-modal-teams"
          title={t('ofTeams')}
          total={teams.length}
          created={teamsCreatedCount}
          deleted={teamsDeletedCount}
          edited={teamsEditedCount}
          unchanged={teamsUnchangedCount}
        />
      </ChangesInfoWrapper>
      {employeesAddedToSurveys.length > 0 && (
        <AddedToSurveysInfoWrapper>
          {employeesAddedToSurveys.map((survey) => {
            const surveyTotalCredits =
              survey.creditsPerParticipant !== null ? survey.count * survey.creditsPerParticipant : null;

            return (
              <div key={survey.name}>
                <span>
                  {t('addedTo')} <strong>{survey.name}</strong>
                </span>
                <span>
                  {survey.count}
                  {isCreditsOrg && surveyTotalCredits !== null && (
                    <>
                      {' ('}
                      {formatCreditsLabel(surveyTotalCredits)}
                      {')'}
                    </>
                  )}
                </span>
              </div>
            );
          })}
          {isCreditsOrg && totalCredits > 0 && (
            <TotalPriceRow>
              <span style={{ fontWeight: 'bold' }}>{t('priceLabel')}</span>
              <span>{formatCreditsLabel(totalCredits)}</span>
            </TotalPriceRow>
          )}
        </AddedToSurveysInfoWrapper>
      )}
    </>
  );

  const nextStepClicked = () => {
    if (currentStep === StepType.CheckEditedRespondents) {
      const hasNoChoice = employeesWithUnsureState.some((employee) => {
        return employee.possibleMatches?.some((match) => {
          const matchChoice = matchChoicesByUser.find((m) => m.newEmployee === employee && m.oldEmployee === match);
          if (matchChoice === undefined) {
            const isAlreadyEditedByDifferentMatch = matchChoicesByUser.some(
              (m) =>
                m.choice === EmployeeImportState.Edited &&
                ((m.newEmployee === employee && m.oldEmployee !== match) ||
                  (m.oldEmployee === match && m.newEmployee !== employee)),
            );
            return !isAlreadyEditedByDifferentMatch;
          }
          return false;
        });
      });
      if (hasNoChoice) {
        setCheckEditedRespondentsMarkRed(checkEditedRespondentsMarkRed + 1);
        return;
      }
    }
    setCurrentStepIdx(currentStepIdx + 1);
    setNextButtonIsDisabled(false);
  };

  const previousStepClicked = () => {
    setCurrentStepIdx(currentStepIdx - 1);
    setNextButtonIsDisabled(false);
  };

  // newly created employees are those who are either new or not sure and not marked as edited
  // which means not connected to any old employee
  const newlyCreatedEmployees = [
    ...employeesWithAdditionalData.filter((employee) => employee.state === EmployeeImportState.New),
    ...employeesWithAdditionalData
      .filter((employee) => employee.state === EmployeeImportState.NotSure)
      .filter((employee) => {
        const wasMarkedAsEdited = matchChoicesByUser.some(
          (m) => m.newEmployee === employee && m.choice === EmployeeImportState.Edited,
        );
        return !wasMarkedAsEdited;
      }),
  ].filter((employee) => employee.surveyParticipant);

  return (
    <Modal
      width={740}
      maxHeight="90vh"
      hideScroll
      dataCy="organization-import-save-modal"
      show
      onHide={currentStep !== StepType.Importing ? onHide : () => 0}
      content={
        <ModalContent>
          <ModalTitle>
            {t('modalTitle', {
              organizationName: organization.name,
            })}
          </ModalTitle>
          <StepContentWrapper border={currentStep !== StepType.Importing}>
            <StepContent>
              {currentStep === StepType.CheckEditedRespondents && <div>{checkEditedRespondentsStepContent}</div>}
              {currentStep === StepType.Onboarding && (
                <AddToOnboardingOrProcessSurveyModal
                  employees={newlyCreatedEmployees}
                  processSurveys={onboardings}
                  scheduledProcessSurveys={onboardingsScheduledForEmployees}
                  setScheduledProcessSurveys={setOnboardingsScheduledForRespondents}
                  setNextButtonIsDisabled={setNextButtonIsDisabled}
                  heading={t('organizationImportCard:addToOnboardingTitle')}
                  processSurveyColumnHeader={t('organizationImportCard:onboarding')}
                />
              )}
              {currentStep === StepType.OneTimeSurvey && (
                <AddToOneTimeSurveysModal
                  employees={newlyCreatedEmployees}
                  surveys={ongoingSurveys}
                  scheduledSurveys={oneTimeSurveysScheduledForEmployees}
                  setScheduledSurveys={setOneTimeSurveysScheduledForRespondents}
                />
              )}
              {currentStep === StepType.ProcessSurvey && (
                <AddToOnboardingOrProcessSurveyModal
                  employees={newlyCreatedEmployees}
                  processSurveys={processSurveys}
                  scheduledProcessSurveys={processSurveysScheduledForEmployees}
                  setScheduledProcessSurveys={setProcessSurveysScheduledForRespondents}
                  setNextButtonIsDisabled={setNextButtonIsDisabled}
                  heading={t('organizationImportCard:addToProcessSurveyTitle')}
                  processSurveyColumnHeader={t('surveysOverview:processTopicGroup')}
                />
              )}
              {currentStep === StepType.ImportInfo && ImportInfoStepContent}
              {currentStep === StepType.Importing && <ImportingProgress percentage={importingProgressPercentage} />}
            </StepContent>
          </StepContentWrapper>
          {currentStep !== StepType.Importing && (
            <ModalFooter>
              <Button
                variant="outline-primary"
                onClick={() => (currentStepIdx === 0 ? onHide() : previousStepClicked())}
              >
                {t(currentStepIdx === 0 ? 'modal:cancel' : 'common:back')}
              </Button>
              <StepProgress>
                {allSteps.slice(0, allSteps.length - 1).map((step, index) => (
                  <StepProgressDot key={index} filled={index <= currentStepIdx}>
                    <DeprecatedCheckIcon />
                  </StepProgressDot>
                ))}
              </StepProgress>
              <Button
                variant="primary"
                onClick={nextStepClicked}
                disabled={nextButtonIsDisabled}
                data-cy={
                  currentStep === StepType.ImportInfo
                    ? 'organization-update-organization-button'
                    : 'organization-import-next-button'
                }
              >
                {t(currentStep === StepType.ImportInfo ? 'updateOrganization' : 'common:next')}
              </Button>
            </ModalFooter>
          )}
        </ModalContent>
      }
    />
  );
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ModalTitle = styled(HeadingMedium)`
  margin: 0 0 ${theme.spacing.f} 0;
  width: 100%;
`;

// We need to have this wrapper just because the scrollbar would pop out of the rounded corners otherwise
const StepContentWrapper = styled.div<{ border?: boolean }>`
  width: 100%;
  overflow: hidden;
  border-radius: ${theme.borderRadius.l};
  border: 1px solid ${(props) => (props.border ? theme.colors.borderMain.default : 'transparent')};
  margin: 0 0 ${theme.spacing.f} 0;
`;

// 280px reserves modal padding, title, step margins, and footer
const StepContent = styled.div`
  max-height: min(50vh, max(160px, calc(90vh - 280px)));
  overflow-y: auto;
  padding: ${theme.spacing.h};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  position: relative;
  button {
    margin: 0;
  }
`;

const StepProgress = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const StepProgressDot = styled.div<{ filled: boolean }>`
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background-color: ${(props) => (props.filled ? theme.colors.brand.primary : theme.colors.backgroundBasic.default)};
  border: 1px solid ${(props) => (props.filled ? theme.colors.brand.primary : theme.colors.borderMain.default)};
  margin: 0 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  path {
    fill: ${(props) => (props.filled ? 'white' : 'transparent')};
  }
`;

const MatchChoiceComponent = ({
  newEmployee,
  oldEmployee,
  choice,
  onChoiceChange,
  redMarkNoChoice,
}: {
  newEmployee: EmployeeRecordWithAdditionalData;
  oldEmployee: EmployeeRecordWithId;
  choice: EmployeeImportState.Edited | EmployeeImportState.New | undefined;
  onChoiceChange: (choice: EmployeeImportState.Edited | EmployeeImportState.New) => void;
  redMarkNoChoice: number;
}) => {
  const [t] = useTranslation('organizationImportModals');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current && redMarkNoChoice && choice === undefined) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [ref, redMarkNoChoice, choice]);

  // we want to render only keys of the oldEmployee coz the new one have more fields (surveys, onboardings, etc.)
  const keysToRender = Object.keys(oldEmployee).filter((key) => key !== 'id');

  const displayedFieldValue = (value: unknown) => (value === true ? 'Y' : value === false ? 'N' : value);

  const keysThatDiffer = Object.keys(oldEmployee).filter((key) => {
    if (key === 'id') {
      return false; // Ignore ID field
    }
    return !areEmployeeFieldValuesEqual(
      displayedFieldValue(newEmployee[key as keyof EmployeeRecordWithId]),
      displayedFieldValue(oldEmployee[key as keyof EmployeeRecordWithId]),
    );
  });

  // Create a unique name for the radio group based on the employee data
  const radioGroupName = `employee-match-choice-${newEmployee.firstname}-${newEmployee.surname}-${
    newEmployee.email || ''
  }-${newEmployee.phoneNumber || ''}-${oldEmployee.id}`;

  return (
    <MatchChoiceWrapper ref={ref} data-cy="match-choice-wrapper">
      <DiffScrollView>
        {[oldEmployee, newEmployee].map((employee, i) => (
          <Row>
            <RowLabelContainer>
              <RowLabel original={i === 0}>
                {i === 0 ? t('organizationImportCard:original') : t('organizationImportCard:new')}
              </RowLabel>
            </RowLabelContainer>
            <TableRow>
              {Object.entries(employee).map(([key, value], u) => {
                if (!keysToRender.includes(key)) {
                  return null; // Skip keys not in the render list
                }
                const isDifferent = keysThatDiffer.includes(key);
                value = value === true ? 'Y' : value === false ? 'N' : value;
                return (
                  <TableCell
                    key={key}
                    width={getOrganizationImportColumnWidth(key)}
                    isDifferent={isDifferent}
                    original={i === 0}
                    isFirst={u === 0}
                    isLast={u === keysToRender.length - 1}
                  >
                    {value}
                  </TableCell>
                );
              })}
            </TableRow>
          </Row>
        ))}
      </DiffScrollView>
      <MatchChoiceFooter>
        <BodyMedium color={theme.colors.text.secondary}>{t('employeesWithChangedDataSelect')}</BodyMedium>
        <RadioInputs>
          <FormRadioInput
            name={radioGroupName}
            dataCy="employee-match-choice-keep"
            clicked={() => onChoiceChange(EmployeeImportState.Edited)}
            defaultChecked={choice === EmployeeImportState.Edited}
            label={t('employeesWithChangedDataKeep')}
          />
          <FormRadioInput
            name={radioGroupName}
            dataCy="employee-match-choice-create"
            clicked={() => onChoiceChange(EmployeeImportState.New)}
            defaultChecked={choice === EmployeeImportState.New}
            label={t('employeesWithChangedDataCreate')}
          />
        </RadioInputs>
        {!!redMarkNoChoice && choice === undefined && (
          <BodyMedium style={{ color: theme.colors.emotionDanger.default, marginTop: theme.spacing.f }}>
            {t('employeesWithChangedDataRedMark')}
          </BodyMedium>
        )}
      </MatchChoiceFooter>
    </MatchChoiceWrapper>
  );
};

const MatchChoiceWrapper = styled.div`
  border: 1px solid ${theme.colors.borderMain.default};
  border-radius: ${theme.borderRadius.l};
  margin-top: 24px;
`;

const DiffScrollView = styled.div`
  overflow-x: auto;
  padding: ${theme.spacing.g};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const RowLabelContainer = styled.div`
  width: 64px;
  flex-shrink: 0;
`;

const RadioInputs = styled.div`
  margin-top: ${theme.spacing.e};
  & > *:first-child {
    margin-bottom: ${theme.spacing.e};
  }
`;

const RowLabel = styled.div<{ original: boolean }>`
  color: white;
  background-color: ${(props) =>
    props.original ? theme.colors.emotionDanger.default : theme.colors.emotionSuccess.default};
  font-size: 12px;
  padding: 0 4px;
  border-radius: ${theme.borderRadius.s};
  width: fit-content;
  font-weight: bold;
`;

const TableRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const TableCell = styled.div<{
  isDifferent: boolean;
  original: boolean;
  isFirst: boolean;
  isLast: boolean;
  width: number;
}>`
  width: ${(props) => props.width + 16}px;
  padding: ${theme.spacing.d};
  font-size: 14px;
  border: 1px solid
    ${(props) =>
      props.isDifferent
        ? props.original
          ? theme.colors.emotionDanger.default
          : theme.colors.emotionSuccess.default
        : theme.colors.borderMain.default};
  background-color: ${(props) =>
    props.isDifferent
      ? props.original
        ? theme.colors.emotionDanger.background
        : theme.colors.emotionSuccess.background
      : 'transparent'};
  border-radius: ${(props) => {
    if (props.isFirst && props.original) return '8px 0 0 0';
    if (props.isLast && props.original) return '0 8px 0 0';
    if (props.isFirst && !props.original) return '0 0 0 8px';
    if (props.isLast && !props.original) return '0 0 8px 0';
    return '0';
  }};
  margin-right: ${(props) => (props.isLast ? theme.spacing.g : 0)};
`;

const MatchChoiceFooter = styled.div`
  padding: ${theme.spacing.g};
  background-color: ${theme.colors.backgroundBasic.default};
  border-radius: ${theme.borderRadius.l};
`;

const ChangesInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: ${theme.spacing.h};
  margin: ${theme.spacing.h} 0;
`;

const ChangesInfoCard = styled.div`
  width: 100%;
  border: 1px solid ${theme.colors.borderMain.default};
  border-radius: ${theme.borderRadius.l};
  & > * {
    padding: ${theme.spacing.e} ${theme.spacing.f};
    border-bottom: 1px solid ${theme.colors.borderMain.default};
  }
  & > *:first-child {
    padding: ${theme.spacing.f};
  }
  & > *:last-child {
    border-bottom: none;
  }
`;

const ChangesInfoRow = styled.div<{ color?: string; bgColor?: string }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  color: ${(props) => props.color || theme.colors.text.primary};
  background-color: ${(props) => props.bgColor || 'none'};
`;

const ChangesInfo = ({
  title,
  dataCy,
  total,
  created,
  deleted,
  edited,
  unchanged,
}: {
  title: string;
  dataCy: string;
  total: number;
  created: number;
  deleted: number;
  edited: number;
  unchanged: number;
}) => {
  const [t] = useTranslation('organizationImportModals');
  return (
    <ChangesInfoCard>
      <BodyMedium color={theme.colors.text.secondary}>
        <span style={{ fontWeight: 'bold', fontSize: '20px', color: theme.colors.text.primary }}>{total}</span>{' '}
        {'\u00A0'}
        {title}
      </BodyMedium>
      <ChangesInfoRow color={theme.colors.emotionSuccess.default} bgColor={theme.colors.emotionSuccess.background}>
        <span>{t('ofCreated')}</span>
        <span data-cy={`${dataCy}-created-count`}>{created}</span>
      </ChangesInfoRow>
      <ChangesInfoRow color={theme.colors.emotionDanger.default} bgColor={theme.colors.emotionDanger.background}>
        <span>{t('ofDeleted')}</span>
        <span data-cy={`${dataCy}-deleted-count`}>{deleted}</span>
      </ChangesInfoRow>
      <ChangesInfoRow>
        <span>{t('ofChanged')}</span>
        <span data-cy={`${dataCy}-edited-count`}>{edited}</span>
      </ChangesInfoRow>
      <ChangesInfoRow>
        <span>{t('ofUnchanged')}</span>
        <span data-cy={`${dataCy}-unchanged-count`}>{unchanged}</span>
      </ChangesInfoRow>
    </ChangesInfoCard>
  );
};

const AddedToSurveysInfoWrapper = styled.div`
  border-radius: ${theme.borderRadius.l};
  border: 1px solid ${theme.colors.borderMain.default};

  & > * {
    padding: ${theme.spacing.d} ${theme.spacing.f};
    border-bottom: 1px solid ${theme.colors.borderMain.default};
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    *:last-child {
      font-weight: bold;
    }
  }
  & > *:last-child {
    border-bottom: none;
  }
`;

const TotalPriceRow = styled.div`
  background-color: ${theme.colors.backgroundBasic.default};
`;

const ImportingProgressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const LabelPercentageWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  margin-bottom: ${theme.spacing.c};
`;

const Percentage = styled.span`
  color: ${theme.colors.actionPrimary.default};
  font-weight: bold;
`;

const LoadingBarWhole = styled.div`
  width: 100%;
  height: 5px;
  background-color: ${theme.colors.borderMain.default};
  border-radius: ${theme.borderRadius.m};
`;

const LoadingBarProgress = styled.div<{ percentage: number }>`
  width: ${(props) => props.percentage}%;
  height: 100%;
  background-color: ${theme.colors.actionPrimary.default};
  border-radius: ${theme.borderRadius.m};
  transition: width 0.5s linear;
`;

const ImportingProgress = ({ percentage }: { percentage: number }) => {
  const [t] = useTranslation('organizationImportCard');
  return (
    <ImportingProgressWrapper>
      <LabelPercentageWrapper>
        <BodySmall>{t('teamImportTeamsPage')}</BodySmall>
        <Percentage>{percentage}%</Percentage>
      </LabelPercentageWrapper>
      <LoadingBarWhole>
        <LoadingBarProgress percentage={percentage} />
      </LoadingBarWhole>
    </ImportingProgressWrapper>
  );
};

enum StepType {
  CheckEditedRespondents = 'checkEditedRespondents',
  Onboarding = 'onboarding',
  ProcessSurvey = 'processSurvey',
  OneTimeSurvey = 'oneTimeSurvey',
  ImportInfo = 'importInfo',
  Importing = 'importing',
}
